import React, { useState, useEffect, useRef, useCallback } from 'react';
import { itemsAPI, priceBookAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Package, Search, Plus, Trash2, Save, Loader2, Boxes, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const MODE_INFO = {
  none: { label: 'No Inventory Impact', desc: 'Diagnostic fee, labor, trip charge, etc.', color: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400' },
  direct: { label: 'Single Inventory Item', desc: 'Maps 1:1 to one catalog part with a default quantity.', color: 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-400' },
  recipe: { label: 'Material Recipe', desc: 'Consumes multiple inventory items per unit sold.', color: 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-400' },
};

/** Typeahead search for catalog items */
function CatalogSearch({ onSelect, placeholder = 'Search inventory catalog...', exclude = [] }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    const handle = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const doSearch = useCallback(async (q) => {
    setQuery(q);
    if (q.length < 2) { setResults([]); setOpen(false); return; }
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setSearching(true);
    try {
      const res = await itemsAPI.search(q, 15, controller.signal);
      const items = (res.data?.items || res.data || []).filter(i => !exclude.includes(i.id));
      setResults(items);
      setOpen(items.length > 0);
    } catch { /* abort */ }
    setSearching(false);
  }, [exclude]);

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/50" />
        <Input
          value={query}
          onChange={e => doSearch(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder={placeholder}
          className="pl-8 h-8 text-sm"
          data-testid="catalog-search-input"
        />
        {searching && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-muted-foreground/50" />}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-popover border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="catalog-search-results">
          {results.map(item => (
            <button
              key={item.id}
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors flex items-center gap-2"
              onClick={() => { onSelect(item); setQuery(''); setResults([]); setOpen(false); }}
              data-testid={`catalog-result-${item.id}`}
            >
              <Package className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{item.name}</div>
                <div className="text-xs text-muted-foreground flex gap-2">
                  {item.sku && <span className="font-mono">{item.sku}</span>}
                  {item.unit_cost != null && <span>${Number(item.unit_cost).toFixed(2)}</span>}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * InventoryModeEditor — Manages inventory consumption config for a PriceBookItem.
 *
 * Props:
 *  - item: full PB item object (with inventory_mode, inventory_item_id, material_recipes, etc.)
 *  - onUpdate: (updatedItem) => void — called after any save
 *  - canEdit: boolean
 */
export function InventoryModeEditor({ item, onUpdate, canEdit }) {
  const mode = item.inventory_mode || 'none';
  const [saving, setSaving] = useState(false);
  const [recipeDraft, setRecipeDraft] = useState(null); // non-null = editing

  const changeMode = async (newMode) => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const updates = { inventory_mode: newMode };
      // Clear direct-mapping fields when switching away from direct
      if (newMode !== 'direct') {
        updates.inventory_item_id = null;
        updates.inventory_default_qty = 1;
      }
      const res = await priceBookAPI.updateItem(item.id, updates);
      onUpdate(res.data);
      // If switching away from recipe, clear recipes
      if (newMode !== 'recipe' && (item.material_recipes || []).length > 0) {
        await priceBookAPI.bulkSaveRecipes(item.id, []).catch(() => {});
      }
    } catch {
      toast.error('Failed to update inventory mode');
    }
    setSaving(false);
  };

  const setDirectItem = async (catalogItem) => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const res = await priceBookAPI.updateItem(item.id, {
        inventory_item_id: catalogItem.id,
        inventory_mode: 'direct',
      });
      onUpdate(res.data);
      toast.success(`Linked to ${catalogItem.name}`);
    } catch {
      toast.error('Failed to link inventory item');
    }
    setSaving(false);
  };

  const updateDefaultQty = async (qty) => {
    if (!canEdit || qty < 1) return;
    setSaving(true);
    try {
      const res = await priceBookAPI.updateItem(item.id, { inventory_default_qty: qty });
      onUpdate(res.data);
    } catch {
      toast.error('Failed to update');
    }
    setSaving(false);
  };

  const unlinkDirect = async () => {
    setSaving(true);
    try {
      const res = await priceBookAPI.updateItem(item.id, { inventory_item_id: null });
      onUpdate(res.data);
    } catch {
      toast.error('Failed to unlink');
    }
    setSaving(false);
  };

  // Recipe editing
  const recipes = item.material_recipes || [];

  const addRecipeLine = async (catalogItem) => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const res = await priceBookAPI.addRecipeLine(item.id, {
        inventory_item_id: catalogItem.id,
        quantity: 1,
        sort_order: recipes.length,
      });
      onUpdate(res.data);
      toast.success(`Added ${catalogItem.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add');
    }
    setSaving(false);
  };

  const removeRecipeLine = async (recipeId) => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const res = await priceBookAPI.deleteRecipeLine(recipeId);
      onUpdate(res.data);
    } catch {
      toast.error('Failed to remove');
    }
    setSaving(false);
  };

  const updateRecipeQty = async (recipeId, qty) => {
    if (!canEdit || qty < 1) return;
    // Use bulk save to update quantity
    const updatedLines = recipes.map(r =>
      r.id === recipeId
        ? { inventory_item_id: r.inventory_item_id, quantity: qty, sort_order: r.sort_order, notes: r.notes }
        : { inventory_item_id: r.inventory_item_id, quantity: r.quantity, sort_order: r.sort_order, notes: r.notes }
    );
    setSaving(true);
    try {
      const res = await priceBookAPI.bulkSaveRecipes(item.id, updatedLines);
      onUpdate(res.data);
    } catch {
      toast.error('Failed to update quantity');
    }
    setSaving(false);
  };

  const existingItemIds = recipes.map(r => r.inventory_item_id);

  return (
    <Card data-testid="inventory-mode-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Boxes className="w-4 h-4" /> Inventory Consumption
          </CardTitle>
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Mode selector */}
        {canEdit ? (
          <div>
            <Label className="text-xs text-muted-foreground mb-1.5 block">When this item is sold, how does it affect inventory?</Label>
            <Select value={mode} onValueChange={changeMode} data-testid="inventory-mode-select">
              <SelectTrigger data-testid="inventory-mode-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(MODE_INFO).map(([key, info]) => (
                  <SelectItem key={key} value={key}>{info.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">{MODE_INFO[mode]?.desc}</p>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Badge className={MODE_INFO[mode]?.color}>{MODE_INFO[mode]?.label}</Badge>
          </div>
        )}

        {/* Direct mode: single item picker + qty */}
        {mode === 'direct' && (
          <div className="space-y-3 border-t pt-3" data-testid="direct-mode-config">
            {item.inventory_item_id ? (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/40 border border-border/50">
                <Package className="w-5 h-5 text-blue-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate" data-testid="direct-item-name">{item.inventory_item_name}</p>
                  {item.inventory_item_sku && <p className="text-xs text-muted-foreground font-mono">{item.inventory_item_sku}</p>}
                  {item.inventory_unit_cost != null && (
                    <p className="text-xs text-muted-foreground">Unit cost: ${Number(item.inventory_unit_cost).toFixed(2)}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-right">
                    <Label className="text-[10px] text-muted-foreground block">Qty</Label>
                    <Input
                      type="number"
                      min="1"
                      value={item.inventory_default_qty || 1}
                      onChange={e => {
                        const v = parseInt(e.target.value);
                        if (v >= 1) updateDefaultQty(v);
                      }}
                      className="h-7 w-14 text-sm text-center"
                      data-testid="direct-qty-input"
                      disabled={!canEdit}
                    />
                  </div>
                  {canEdit && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={unlinkDirect} data-testid="unlink-direct-btn">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">Link a catalog item</Label>
                <CatalogSearch onSelect={setDirectItem} placeholder="Search for an inventory item..." />
              </div>
            )}
          </div>
        )}

        {/* Recipe mode: multi-item list */}
        {mode === 'recipe' && (
          <div className="space-y-3 border-t pt-3" data-testid="recipe-mode-config">
            {recipes.length > 0 && (
              <div className="rounded-lg border border-border/50 overflow-hidden">
                <table className="w-full text-sm" data-testid="recipe-table">
                  <thead>
                    <tr className="bg-muted/30 text-xs text-muted-foreground">
                      <th className="text-left py-1.5 px-3">Inventory Item</th>
                      <th className="text-center py-1.5 px-2 w-20">Qty</th>
                      {canEdit && <th className="w-10"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {recipes.map((rl, idx) => (
                      <tr key={rl.id} className="border-t border-border/30 group" data-testid={`recipe-line-${idx}`}>
                        <td className="py-2 px-3">
                          <div className="flex items-center gap-2">
                            <Package className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                            <div className="min-w-0">
                              <span className="font-medium truncate block">{rl.inventory_item_name || 'Unknown'}</span>
                              {rl.inventory_item_sku && <span className="text-xs text-muted-foreground font-mono">{rl.inventory_item_sku}</span>}
                            </div>
                          </div>
                        </td>
                        <td className="py-2 px-2 text-center">
                          <Input
                            type="number"
                            min="1"
                            value={rl.quantity}
                            onChange={e => {
                              const v = parseInt(e.target.value);
                              if (v >= 1) updateRecipeQty(rl.id, v);
                            }}
                            className="h-7 w-16 text-sm text-center mx-auto"
                            data-testid={`recipe-qty-${idx}`}
                            disabled={!canEdit}
                          />
                        </td>
                        {canEdit && (
                          <td className="py-2 pr-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => removeRecipeLine(rl.id)}
                              data-testid={`recipe-remove-${idx}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {canEdit && (
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">
                  {recipes.length === 0 ? 'Add inventory items consumed when this is sold' : 'Add another item'}
                </Label>
                <CatalogSearch
                  onSelect={addRecipeLine}
                  placeholder="Search catalog to add..."
                  exclude={existingItemIds}
                />
              </div>
            )}

            {recipes.length === 0 && (
              <div className="text-center py-4 text-muted-foreground">
                <Boxes className="w-6 h-6 mx-auto mb-1.5 opacity-30" />
                <p className="text-xs">No recipe items yet. Search above to add parts this item consumes.</p>
              </div>
            )}

            {recipes.length > 0 && (
              <div className="text-xs text-muted-foreground bg-muted/30 rounded px-3 py-2">
                When this item is sold (qty=1), the following will be expected:
                {recipes.map(r => (
                  <span key={r.id} className="inline-flex items-center gap-1 ml-1">
                    <ArrowRight className="w-3 h-3" />{r.quantity}x {r.inventory_item_name || '?'}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
